import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import { RefreshCw, CheckCircle2, XCircle, Circle, Search, Download, MoreVertical, Ban, Play, Edit3, Upload, Plus, Send } from 'lucide-react';
import { toast } from 'sonner';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function Pill({ ok, children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 border rounded-sm ${
      ok ? 'border-emerald-500/40 text-emerald-400 bg-emerald-500/5' : 'border-white/10 text-zinc-500'
    }`}>
      {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
      {children}
    </span>
  );
}

const FILTERS = [
  { key: 'all', label: 'All', params: {} },
  { key: 'beta', label: 'Beta', params: { account_status: 'beta' } },
  { key: 'free', label: 'Free', params: { account_status: 'free' } },
  { key: 'paid', label: 'Paid', params: { account_status: 'paid' } },
  { key: 'canceled', label: 'Canceled', params: { account_status: 'canceled' } },
  { key: 'suspended', label: 'Suspended', params: { account_status: 'suspended' } },
  { key: 'connected', label: 'Alpaca Connected', params: { alpaca_connected: true } },
  { key: 'not_connected', label: 'Not Connected', params: { alpaca_connected: false } },
  { key: 'auto_on', label: 'Auto-Trading On', params: { paper_auto_trading: true } },
  { key: 'recent', label: 'Recent (7d)', params: { days: 7 } },
];

const STATUSES = ['beta', 'free', 'paid', 'canceled', 'suspended'];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...(FILTERS.find(f => f.key === filter)?.params || {}) };
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get('/admin/users', { params });
      setUsers(data.items || []);
    } catch (e) {
      console.error('[admin/users] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const handlePatch = async (userId, patch, successMsg) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, patch);
      toast.success(successMsg || 'User updated.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    }
  };

  const exportUrl = useMemo(() => `${api.defaults.baseURL}/admin/users/export`, []);

  return (
    <AdminLayout title="Users" description="Beta accounts, Alpaca state, plan tier, and per-user execution stats. Manage status, disable accounts, leave admin notes.">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <input
              type="search"
              placeholder="Search by email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-users-search"
              className="pl-9 pr-3 py-2 w-64 border border-white/10 rounded-sm bg-[#0c0c0e] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-[#d8b34a]/50"
            />
          </div>
          <p className="text-sm text-zinc-500 font-mono ml-2">{loading ? '…' : `${users.length}`}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAdd(true)} data-testid="admin-users-add-single" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm transition-colors">
            <Plus size={13} /> Add user
          </button>
          <button onClick={() => setShowImport(true)} data-testid="admin-users-import" className="inline-flex items-center gap-2 text-sm px-4 py-2 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm font-medium">
            <Upload size={13} /> Bulk import
          </button>
          <a href={exportUrl} data-testid="admin-users-export" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm transition-colors">
            <Download size={13} /> Export CSV
          </a>
          <button onClick={load} data-testid="admin-users-refresh" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm transition-colors">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="admin-users-filters">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`admin-users-filter-${f.key}`}
            className={`text-xs px-3 py-1.5 border rounded-sm transition-all font-mono ${
              filter === f.key ? 'border-[#d8b34a]/50 text-white bg-[#d8b34a]/[0.06]' : 'border-white/10 text-zinc-400 hover:text-white'
            }`}
          >
            {f.label.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="border border-white/10 rounded-md overflow-hidden bg-[#0c0c0e]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/10">
              <tr className="text-left text-[10px] tracking-[0.3em] font-mono text-zinc-500">
                <th className="px-4 py-3">EMAIL</th>
                <th className="px-4 py-3">STATUS</th>
                <th className="px-4 py-3">PLAN</th>
                <th className="px-4 py-3">ALPACA</th>
                <th className="px-4 py-3">AUTO</th>
                <th className="px-4 py-3">STATS</th>
                <th className="px-4 py-3">LATEST</th>
                <th className="px-4 py-3">JOINED</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="admin-users-tbody">
              {users.map((u) => (
                <tr key={u.id} className={`border-t border-white/5 hover:bg-white/[0.02] ${u.disabled ? 'opacity-50' : ''}`} data-testid={`admin-user-row-${u.email}`}>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-100 font-medium">{u.email}</span>
                      {u.role === 'admin' && <span className="text-[10px] gold-text font-mono">ADMIN</span>}
                      {u.disabled && <span className="text-[10px] text-red-400 font-mono">DISABLED</span>}
                    </div>
                    {u.admin_notes && <p className="text-[10px] text-zinc-500 mt-1 italic max-w-[200px] truncate">"{u.admin_notes}"</p>}
                  </td>
                  <td className="px-4 py-3.5">
                    <StatusPill status={u.account_status} />
                  </td>
                  <td className="px-4 py-3.5 text-xs text-zinc-300">{u.plan_name}</td>
                  <td className="px-4 py-3.5"><Pill ok={u.alpaca_connected}>{u.alpaca_connected ? (u.alpaca_env || 'paper').toUpperCase() : 'OFF'}</Pill></td>
                  <td className="px-4 py-3.5"><Pill ok={u.paper_auto_trading}>{u.paper_auto_trading ? 'ON' : 'OFF'}</Pill></td>
                  <td className="px-4 py-3.5 text-xs font-mono text-zinc-400" data-testid={`stats-${u.email}`}>
                    {u.stats ? `${u.stats.filled}f · ${u.stats.skipped}s · ${u.stats.failed}x` : '—'}
                  </td>
                  <td className="px-4 py-3.5 text-xs">
                    {u.latest_execution ? (
                      <div>
                        <span className="font-mono text-zinc-200">{u.latest_execution.status}</span>
                        {u.latest_execution.symbol && <span className="ml-2 text-zinc-500">{u.latest_execution.symbol}</span>}
                      </div>
                    ) : <span className="text-zinc-600 inline-flex items-center gap-1"><Circle size={9} /> none</span>}
                  </td>
                  <td className="px-4 py-3.5 text-zinc-500 font-mono text-xs">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setEditing(u)}
                        data-testid={`admin-user-edit-${u.email}`}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-white/10 text-zinc-400 hover:text-white hover:border-[#d8b34a]/40 rounded-sm transition-colors"
                      >
                        <MoreVertical size={12} /> Edit
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/admin/users/${u.id}/send-invite`);
                            if (data.result?.status === 'sent') toast.success(`Invite sent to ${u.email}`);
                            else if (data.result?.status === 'skipped') toast.warning(`Logged but skipped (${data.result.error})`);
                            else toast.error(`Invite failed: ${data.result?.error}`);
                          } catch (e) { toast.error('Invite failed.'); }
                        }}
                        data-testid={`admin-user-invite-${u.email}`}
                        title="Send single-use invite link"
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-white/10 gold-text hover:border-[#d8b34a]/40 rounded-sm transition-colors"
                      >
                        <Send size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && users.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-zinc-500">No users match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showImport && <UserImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      {showAdd && <UserAddModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}

      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            await handlePatch(editing.id, patch, 'User updated.');
            setEditing(null);
          }}
          onDisable={() => handlePatch(editing.id, { disabled: !editing.disabled }, editing.disabled ? 'User re-enabled.' : 'User disabled.')}
        />
      )}
    </AdminLayout>
  );
}

function StatusPill({ status }) {
  const tones = {
    beta: 'border-[#d8b34a]/40 text-[#d8b34a] bg-[#d8b34a]/5',
    free: 'border-white/10 text-zinc-300',
    paid: 'border-emerald-500/40 text-emerald-400 bg-emerald-500/5',
    canceled: 'border-amber-500/40 text-amber-400 bg-amber-500/5',
    suspended: 'border-red-500/40 text-red-400 bg-red-500/5',
  };
  return <span className={`text-[10px] font-mono px-2 py-1 border rounded-sm ${tones[status] || 'border-white/10 text-zinc-400'}`}>{(status || '—').toUpperCase()}</span>;
}

function EditUserModal({ user, onClose, onSave, onDisable }) {
  const [account_status, setStatus] = useState(user.account_status || 'beta');
  const [plan_name, setPlan] = useState(user.plan_name || '');
  const [admin_notes, setNotes] = useState(user.admin_notes || '');
  const [saving, setSaving] = useState(false);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-user-modal">
      <div className="w-full max-w-lg border border-white/10 rounded-md bg-[#0c0c0e] p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] tracking-[0.3em] font-mono gold-text">EDIT USER</p>
            <h3 className="font-display mt-1 text-xl font-bold text-white">{user.email}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-xl">×</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">ACCOUNT STATUS</label>
            <select
              value={account_status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="edit-user-status"
              className="mt-2 w-full px-3 py-2 border border-white/10 rounded-sm bg-[#0a0a0b] text-sm text-zinc-100 focus:outline-none focus:border-[#d8b34a]/50"
            >
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">PLAN NAME</label>
            <input
              type="text"
              value={plan_name}
              onChange={(e) => setPlan(e.target.value)}
              data-testid="edit-user-plan"
              className="mt-2 w-full px-3 py-2 border border-white/10 rounded-sm bg-[#0a0a0b] text-sm text-zinc-100 focus:outline-none focus:border-[#d8b34a]/50"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">ADMIN NOTES (internal)</label>
            <textarea
              value={admin_notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="edit-user-notes"
              rows={3}
              className="mt-2 w-full px-3 py-2 border border-white/10 rounded-sm bg-[#0a0a0b] text-sm text-zinc-100 focus:outline-none focus:border-[#d8b34a]/50 resize-none"
            />
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-white/5 flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={onDisable}
            data-testid="edit-user-disable"
            className={`inline-flex items-center gap-2 text-sm px-4 py-2 border rounded-sm transition-colors ${
              user.disabled
                ? 'border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/5'
                : 'border-red-500/40 text-red-400 hover:bg-red-500/5'
            }`}
          >
            {user.disabled ? (<><Play size={13} /> Re-enable</>) : (<><Ban size={13} /> Disable access</>)}
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-sm px-4 py-2 border border-white/10 text-zinc-400 hover:text-white rounded-sm">Cancel</button>
            <button
              onClick={async () => { setSaving(true); await onSave({ account_status, plan_name, admin_notes }); setSaving(false); }}
              disabled={saving}
              data-testid="edit-user-save"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm font-medium transition-colors disabled:opacity-60"
            >
              <Edit3 size={13} /> Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserImportModal({ onClose, onDone }) {
  const [emails, setEmails] = React.useState('');
  const [sendInvite, setSendInvite] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/users/import', { emails, send_invite: sendInvite });
      setResult(data);
      toast.success(`${data.summary.created_count} users created.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="user-import-modal">
      <div className="w-full max-w-xl border border-white/10 rounded-md bg-[#0c0c0e] p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] tracking-[0.3em] font-mono gold-text">BULK IMPORT APP USERS</p>
            <h3 className="font-display mt-1 text-xl font-bold text-white">Add beta accounts</h3>
            <p className="mt-1 text-xs text-zinc-500">Users will be created but cannot sign in until they verify via Google or click an invite link.</p>
          </div>
          <button onClick={onClose} className="text-zinc-500">×</button>
        </div>
        {!result ? (
          <>
            <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">PASTE EMAILS</label>
            <textarea value={emails} onChange={(e) => setEmails(e.target.value)} rows={8} data-testid="user-import-textarea"
              placeholder="alice@example.com&#10;bob@example.com" className={`${inputCls} font-mono resize-none mt-2`} />
            <label className="mt-4 flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} data-testid="user-import-send-invite" />
              Also send invite email to each new user
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="text-sm px-4 py-2 border border-white/10 text-zinc-400 rounded-sm">Cancel</button>
              <button onClick={submit} disabled={busy || !emails.trim()} data-testid="user-import-submit" className="text-sm px-4 py-2 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm disabled:opacity-60">
                {busy ? 'Importing…' : 'Import users'}
              </button>
            </div>
          </>
        ) : (
          <div data-testid="user-import-result">
            <ul className="text-sm space-y-1.5">
              <li className="flex justify-between"><span className="text-zinc-400">Created</span><span className="text-emerald-400 font-mono">{result.summary.created_count}</span></li>
              <li className="flex justify-between"><span className="text-zinc-400">Already existed</span><span className="text-zinc-300 font-mono">{result.summary.already_existed_count}</span></li>
              <li className="flex justify-between"><span className="text-zinc-400">Invalid</span><span className="text-red-400 font-mono">{result.summary.invalid_count}</span></li>
              {result.invite_results?.length > 0 && (
                <li className="flex justify-between"><span className="text-zinc-400">Invites sent</span><span className="text-zinc-300 font-mono">{result.invite_results.filter(r => r.status === 'sent').length} / {result.invite_results.length}</span></li>
              )}
            </ul>
            {!result.smtp_configured && sendInvite && (
              <p className="mt-3 text-xs text-amber-300">SMTP not configured — invite emails were logged as skipped.</p>
            )}
            <div className="mt-5 flex justify-end">
              <button onClick={onDone} data-testid="user-import-done" className="text-sm px-4 py-2 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UserAddModal({ onClose, onDone }) {
  const [email, setEmail] = React.useState('');
  const [sendInvite, setSendInvite] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/admin/users/add', { email, send_invite: sendInvite });
      toast.success(sendInvite ? 'User added + invite sent.' : 'User added.');
      onDone();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'unknown';
      toast.error(`Add failed (${status || 'network'}): ${detail}`);
    }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="user-add-modal">
      <div className="w-full max-w-md border border-white/10 rounded-md bg-[#0c0c0e] p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold text-white">Add app user</h3>
          <button onClick={onClose} className="text-zinc-500">×</button>
        </div>
        <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">EMAIL</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="user-add-email" className={`${inputCls} mt-2`} />
        <label className="mt-4 flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} data-testid="user-add-send-invite" />
          Send invite email
        </label>
        <p className="mt-3 text-xs text-zinc-500">No session is created. User must verify via Google or invite link to access the dashboard.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 border border-white/10 text-zinc-400 rounded-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !email} data-testid="user-add-submit" className="text-sm px-4 py-2 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm disabled:opacity-60">
            {busy ? 'Adding…' : 'Add user'}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 border border-white/10 rounded-sm bg-[#0a0a0b] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-[#d8b34a]/50';
